/**
 * resolveAndRender — 统一的「按 ID 解析并渲染目标」分派器。
 *
 * 历史背景：原先以下三个站点各自实现了一份 lookup-then-render：
 *   1. continuation/ContinuationResolver.tsx (按 ContinuationTarget.kind 分派)
 *   2. blocks/PluginBlock.tsx (按 definition / for-block / content-definition 顺序回退)
 *   3. blocks/ContentBlock.tsx (在自身 scope 中查找)
 *
 * 此 helper 把「拿到一个 targetId / 一个目标 kind 之后、决定渲染哪个组件」的
 * 决策集中到一个地方，新增一种 target kind 只需在这里扩展。
 *
 * 它故意不读 React Context（不是 Hook）—— 由调用方把 DocContextValue 传入，
 * 这样可以在 PluginBlock 内被同步调用，也方便单元测试。
 *
 * 不处理的场景（按当前任务范围保留在原地）：
 *   - PluginBlock 中 scope-chain 解析（scoped definition / scoped content）
 *   - ContinuationResolver 的 fragment 分支（直接走 FragmentRenderer）
 *   - ContentBlock 内部的 BlockList / ScopeProvider 渲染
 *
 * P3 #5 起，content 分支可由 `useExecution` 派生 use-content 渲染，并可选用
 * `wrapContent` 触发 `doc.ContentBlockWrapper` 包装 —— 复刻原 PluginBlock step 5。
 */

import type React from "react";
import { splitScopedMemberPath } from "@magic-md/core";
import type { DocContextValue } from "../contexts/DocContext";
import type {
    ContentExecutionEntry,
    UseExecutionEntry,
} from "../../../store/plugin-execution-store";
import { ContentBlock } from "./ContentBlock";
import { ForBlock } from "./ForBlock";
import { ImportDocumentBlock } from "./ImportDocumentBlock";
import { PluginBlock } from "./PluginBlock";

/** 目标种类 —— 与 ContinuationTarget 的非 fragment 分支保持一致。 */
export type ResolveKind = "definition" | "for" | "content" | "import-document";

export interface ResolveAndRenderOptions {
    /** 要解析的目标 ID（definition / for-block / content-definition 共享 ID 命名空间）。 */
    targetId: string;
    /** ::render 上的属性，会透传给 PluginBlock。 */
    attrs?: Record<string, string>;
    /** 渲染模式，默认 "block"。 */
    mode?: "inline" | "block";
    /** Initial expanded state when attrs.expanded is absent. */
    defaultExpanded?: boolean;
    /**
     * 期望的目标种类。
     *  - 省略：按 definition → for → content 顺序回退（PluginBlock 历史行为）。
     *  - 指定：仅查该种类；查不到返回 `null`，由调用方自行渲染占位符。
     */
    kind?: ResolveKind;
    /**
     * Content 渲染时附带的 execution 数据。
     * ContinuationResolver 路径会传入；PluginBlock 路径在 P3 #5 后也走此 helper，
     * 由 `useExecution` 派生 effective execution / onRun / instanceKind。
     */
    contentExecution?: ContentExecutionEntry;
    /** Content 的 Run 回调（缺省取 doc.onRunContent；如 useExecution 是 content-kind 则取 doc.onRunUse）。 */
    contentOnRun?: (id: string) => void;
    /** Content instanceKind 标签（显式指定优先；否则按 useExecution 自动派生）。 */
    contentInstanceKind?: "content" | "use-content" | "scoped-content";
    /**
     * 关联的 use-execution 条目。仅在 content 分支命中时生效：
     *  - kind === "content" 时派生 use-content 渲染（execution / onRun / instanceKind）。
     *  - 显式 contentExecution / contentOnRun / contentInstanceKind 优先于派生值。
     * 用于复刻 PluginBlock 历史的 step 5 行为。
     */
    useExecution?: UseExecutionEntry;
    /**
     * Content 分支命中后是否用 doc.ContentBlockWrapper 包裹结果。
     * PluginBlock 调用此 helper 时设为 true（保留原 wrapping 行为）；
     * ContinuationResolver 不走 wrapper（保持原行为）。
     */
    wrapContent?: boolean;
}

/**
 * 根据 `targetId` (+ 可选 `kind`) 查 doc 里的 definitions / forBlocksMap /
 * contentDefinitions / importDocuments，并返回对应的 React 元素；找不到返回 `null`。
 *
 * 注意：返回的 `<PluginBlock>` 自身仍然会做 scope-chain 与 use-execution 派发，
 * 这是历史行为；本 helper 只解决「id → 顶层组件」这一层。
 */
export function resolveAndRender(
    opts: ResolveAndRenderOptions,
    doc: DocContextValue,
): React.ReactNode {
    const {
        targetId,
        attrs = {},
        mode = "block",
        defaultExpanded = true,
        kind,
        contentExecution,
        contentOnRun,
        contentInstanceKind,
        useExecution,
        wrapContent = false,
    } = opts;

    const hasRealUnit = (id: string): boolean =>
        doc.definitions.has(id) ||
        !!doc.forBlocksMap?.has(id) ||
        !!doc.contentDefinitions?.has(id) ||
        !!doc.importDocuments?.has(id);

    const scopedContentMember =
        (!kind || kind === "content")
            ? splitScopedMemberPath(
                targetId,
                (id) => !!doc.contentDefinitions?.has(id),
                hasRealUnit,
            )
            : null;

    if (!kind || kind === "definition") {
        if (doc.definitions.has(targetId)) {
            // PluginBlock 内含 PluginRenderSection + scope chain + 进一步 fallback。
            return (
                <PluginBlock
                    definitionId={targetId}
                    attrs={attrs}
                    mode={mode}
                    defaultExpanded={defaultExpanded}
                />
            );
        }
        if (kind === "definition") return null;
    }

    if (!kind || kind === "for") {
        const forBlock = doc.forBlocksMap?.get(targetId);
        if (forBlock) {
            return (
                <ForBlock
                    block={forBlock}
                    mode={mode}
                    attrs={attrs}
                    defaultExpanded={defaultExpanded}
                />
            );
        }
        if (kind === "for") return null;
    }

    if (scopedContentMember) {
        const contentDef = doc.contentDefinitions?.get(scopedContentMember.rootId);
        if (contentDef) {
            const isUseContent = useExecution?.kind === "content";
            const derivedExecution: ContentExecutionEntry | undefined =
                contentExecution ??
                (isUseContent && useExecution
                    ? {
                        contentId: scopedContentMember.rootId,
                        status: useExecution.status,
                        startedAt: useExecution.startedAt,
                        duration: useExecution.duration,
                        error: useExecution.error,
                        output: contentDef.output,
                    }
                    : undefined);
            const derivedOnRun =
                contentOnRun ?? (isUseContent ? doc.onRunUse : doc.onRunContent);
            const derivedInstanceKind =
                contentInstanceKind ?? (isUseContent ? "use-content" : "content");

            const rendered = (
                <ContentBlock
                    content={contentDef}
                    definitionId={scopedContentMember.rootId}
                    execution={derivedExecution}
                    mode={mode}
                    attrs={attrs}
                    defaultExpanded={defaultExpanded}
                    onRun={derivedOnRun}
                    instanceKind={derivedInstanceKind}
                    renderMemberPath={scopedContentMember.memberPath}
                />
            );

            if (wrapContent && doc.ContentBlockWrapper) {
                const Wrapper = doc.ContentBlockWrapper;
                return <Wrapper definitionId={scopedContentMember.rootId}>{rendered}</Wrapper>;
            }
            return rendered;
        }
    }

    if (!kind || kind === "content") {
        const contentDef = doc.contentDefinitions?.get(targetId);
        if (contentDef) {
            const isUseContent = useExecution?.kind === "content";
            // contentExecution 显式优先；否则在 use-content 模式下从 useExecution 派生。
            const derivedExecution: ContentExecutionEntry | undefined =
                contentExecution ??
                (isUseContent && useExecution
                    ? {
                        contentId: targetId,
                        status: useExecution.status,
                        startedAt: useExecution.startedAt,
                        duration: useExecution.duration,
                        error: useExecution.error,
                        output: contentDef.output,
                    }
                    : undefined);
            const derivedOnRun =
                contentOnRun ?? (isUseContent ? doc.onRunUse : doc.onRunContent);
            const derivedInstanceKind =
                contentInstanceKind ?? (isUseContent ? "use-content" : "content");

            const rendered = (
                <ContentBlock
                    content={contentDef}
                    definitionId={targetId}
                    execution={derivedExecution}
                    mode={mode}
                    attrs={attrs}
                    defaultExpanded={defaultExpanded}
                    onRun={derivedOnRun}
                    instanceKind={derivedInstanceKind}
                />
            );

            if (wrapContent && doc.ContentBlockWrapper) {
                const Wrapper = doc.ContentBlockWrapper;
                return <Wrapper definitionId={targetId}>{rendered}</Wrapper>;
            }
            return rendered;
        }
        if (kind === "content") return null;
    }

    if (!kind || kind === "import-document") {
        const snapshot = doc.importDocuments?.get(targetId);
        if (snapshot) {
            return (
                <ImportDocumentBlock
                    namespace={targetId}
                    snapshot={snapshot}
                    mode={mode}
                    attrs={attrs}
                    defaultExpanded={defaultExpanded}
                />
            );
        }
        if (kind === "import-document") return null;
    }

    return null;
}
